/**
 * Storefront error reporter.
 *
 * Sends uncaught JS errors, unhandled promise rejections, failed resource loads
 * and failed cart/checkout calls to app.snowmonkey.com/api/frontend-errors,
 * where they are grouped by fingerprint and turned into one BoardMonkey card per
 * defect.
 *
 * Three rules this file must never break:
 *
 *  1. It can never break the page. Every entry point is wrapped, and any failure
 *     inside the reporter is swallowed. A broken error reporter that takes the
 *     storefront with it is worse than no error reporting.
 *  2. It can never report itself. Its own beacon is excluded from the fetch and
 *     XHR wrappers, or one failure becomes an infinite loop.
 *  3. It is capped. Per unique error and in total, per session — a bug that
 *     throws inside a scroll handler must not turn one visitor into ten thousand
 *     requests.
 *
 * ES5 on purpose: a meaningful share of this traffic is in-app browsers
 * (Facebook, Instagram) on old Android WebViews, and those are exactly the
 * sessions worth measuring.
 */
(function () {
  'use strict';

  var cfg = window.SM_ERROR_REPORTER || {};
  var ENDPOINT = cfg.endpoint || 'https://app.snowmonkey.com/api/frontend-errors';

  if (cfg.enabled === false) return;

  /* Can this engine parse the theme's own scripts?

     They use optional chaining and nullish coalescing, so an engine without them
     fails to parse script after script, and everything it reports afterwards
     follows from that — a global an unparsed file would have defined is simply
     missing. None of it is a defect we can fix on the page. The user agent cannot
     answer this: the client behind eight such cards on 2026-09-23 claimed a
     modern Chrome. So the engine is asked, once, by compiling the syntax itself,
     and every report says so; the backend files the SCRIPT errors among them as
     the visitor's software. A failed request or file load is kept either way.

     Flagged, not silenced — see the header on why these sessions are worth
     measuring. And only a SyntaxError counts: a Content-Security-Policy without
     unsafe-eval refuses to compile at all, which says nothing about the engine. */
  var LEGACY_ENGINE = (function () {
    try {
      new Function('var o = {}; return o?.a ?? 1;');
      return false;
    } catch (err) {
      return !!err && err.name === 'SyntaxError';
    }
  })();

  // Caps. `MAX_KEYS` bounds distinct errors, `MAX_PER_KEY` bounds a hot loop,
  // and together they bound the whole session.
  var MAX_KEYS = 12;
  var MAX_PER_KEY = 25;
  var FLUSH_MS = 4000;

  /* The resource guards below can only answer "what did the server say?" while
     the entry is still in the Resource Timing buffer, and the browser default
     caps that at 250 entries. A live product page here loads 328 subresources
     (measured 2026-09-08), so the buffer overflows mid-load and every later
     failure arrives with nothing recorded — falling through to the page-state
     guard, which is exactly the path that mis-reports cancellations. This file
     is the fifth script on the page, so raising it here catches nearly the whole
     document. */
  try {
    if (window.performance && performance.setResourceTimingBufferSize) {
      performance.setResourceTimingBufferSize(1000);
    }
  } catch (_) { /* never throw out of the reporter */ }

  var pending = {};   // key -> { event, count }
  var counted = {};   // key -> total this session
  var announced = {}; // key -> have we already claimed firstInSession
  var callersOf = {}; // key -> callers batched separately in this window
  var keyCount = 0;
  var timer = null;

  // True while the page is hidden or tearing down. Used to tell a real request
  // failure from the browser cancelling in-flight work as the page goes away —
  // see the AbortError branch in the fetch wrapper below.
  var pageHidden = document.visibilityState === 'hidden';

  // ...but `pageHidden` alone is only ever true in time on WebKit.
  //
  // The engines disagree on ORDER. WebKit fires pagehide first and cancels the
  // in-flight requests after, so the flag is already set when the failure
  // arrives. Chromium does the opposite: it cancels and fires the error FIRST,
  // and only then runs pagehide/visibilitychange — so the flag is still false
  // at the one moment it is read.
  //
  // That ordering, not the error's name, is what split the 2026-08-20 fix in
  // half. Keying on page state instead of err.name took POST /cart/update.js
  // (100% WebKit) to flat within hours, while GET /cart.js (78% Android) was
  // still arriving a day later at twice the rate of the deliberately-unfixed
  // control fingerprints, and "Failed to load script" (56% Windows) only
  // half-decayed. Same guard, same page state, opposite engine.
  //
  // So track the committed intent to leave as well, from events that DO run
  // before Chromium cancels anything. Time-boxed, because a navigation that
  // never commits must not blind the reporter for the rest of the session.
  var LEAVING_MS = 2500;
  var leavingUntil = 0;

  function markLeaving() { leavingUntil = Date.now() + LEAVING_MS; }

  // The one question every reporting path below asks: is this request dying
  // because the page is going away, or because something is actually broken?
  function leaving() {
    return pageHidden || Date.now() < leavingUntil;
  }

  /* Reading leaving() at the moment of failure is still wrong on Chromium, and
     the counters say so. The engines disagree on ORDER (see above): WebKit hides
     first and cancels after, Chromium cancels and rejects FIRST and only then
     runs pagehide. markLeaving() covers the navigations that announce themselves
     via beforeunload or the Navigation API — but the ones that do not still slip
     through, and they are the majority here: "GET /cart.js -> 0" is 831
     sessions, 762 of them Android, 251 of them inside a Facebook or Instagram
     in-app webview, and it did not move when its WebKit twin
     ("POST /cart/update.js -> 0", 100% WebKit) went flat within hours of the
     same guard.

     So stop reading the flag at the one instant it is guaranteed to be stale,
     and read it late instead. A page that is going away either fires pagehide
     inside this delay — leaving() becomes true and the report is dropped — or is
     torn down outright, in which case the timer never runs and it is dropped for
     free. A page that is genuinely alive reports as before, one tick later.

     hideGeneration is what makes this safe across a bfcache restore: pageshow
     resets pageHidden and leavingUntil, so a timer frozen at hide time and
     resumed on Back would otherwise wake up, see a perfectly healthy page, and
     report an hour-old teardown as a live outage. Comparing the generation
     captured at scheduling time catches that — the page hid, so the report is
     dropped no matter what the flags say now. */
  var LEAVING_SETTLE_MS = 400;
  var hideGeneration = 0;

  /* `startGeneration` asks the same question backwards, over the whole life of
     the request, and it is the half that was missing.

     Reading the flags when the rejection arrives only ever asks "is the page
     going away NOW". That cannot see the shape which produces most of this
     traffic: the request starts on a visible page, the visitor backgrounds the
     tab, the browser cancels or freezes the request, the visitor comes BACK, and
     only then does the rejection surface — by which point pageshow /
     visibilitychange have reset pageHidden and leavingUntil. A perfectly healthy
     page then reports an hour-old teardown as a live outage.

     hideGeneration was introduced for precisely this case and could not catch
     it, because the generation was captured HERE, at rejection time: the hide it
     was meant to detect had already been counted on both sides of the
     comparison. Captured when the REQUEST STARTS instead, a mismatch means "the
     page hid at some point while this was in flight", which is teardown no
     matter how healthy the page looks by the time we are asked.

     What made this visible: timeouts are essentially exclusive to GET /cart.js —
     691 of 697 timed-out sessions — while POST /cart/add and POST /cart/update.js,
     same host and same sessions, have zero. No network is that selective. What
     separates them is the window. The cart writes fire on a tap and settle in
     under a second; /cart.js is driven by the engraving reconcile's retry loops,
     which span ~9s and keep running after the visitor's attention has moved on.

     Callers with no meaningful start (a resource load error) omit it, which
     leaves their behaviour exactly as it was. */
  function reportUnlessLeaving(evt, startGeneration) {
    var gen = typeof startGeneration === 'number' ? startGeneration : hideGeneration;
    if (leaving() || hideGeneration !== gen) return;
    setTimeout(function () {
      if (leaving() || hideGeneration !== gen) return;
      report(evt);
    }, LEAVING_SETTLE_MS);
  }

  // Exported because cart.js needs the same answer for a non-network path: its
  // engraving reconcile throws after ~9s of failed /cart.js reads, and every one
  // of those reads fails the instant the browser freezes the page. That arrives
  // as an unhandledrejection, which never passes through the network guards
  // below, so it was reported as a real outage. Sharing the function rather than
  // re-deriving it there keeps ONE definition of the engine-ordering rule
  // documented above — a second copy is exactly how the 2026-08-20 fix ended up
  // half-right on WebKit and wrong on Chromium.
  window.__smPageLeaving = leaving;

  function ctx() {
    var s = window.Shopify || {};
    return {
      page: String(location.pathname || '').slice(0, 300),
      template: cfg.template || '',
      locale: s.locale || document.documentElement.lang || '',
      country: (s.country || cfg.country || '') + ''
    };
  }

  function keyOf(e) {
    if (e.kind === 'network') {
      // timedOut is part of the key, matching the server's fingerprint. Without
      // it a timeout and a cancelled request batch together here, and whichever
      // arrived first decides the flag for both — which would undo the split
      // before the payload was even sent.
      return 'n|' + e.request.method + '|' + String(e.request.url).split('?')[0] + '|' + e.request.status +
             (e.request.timedOut ? '|timeout' : '');
    }
    return 'j|' + String(e.message).slice(0, 200) + '|' + String(e.source || '').split('?')[0];
  }

  function flush(useBeacon) {
    try {
      var events = [];
      for (var k in pending) {
        if (Object.prototype.hasOwnProperty.call(pending, k)) {
          var p = pending[k];
          p.event.count = p.count;
          events.push(p.event);
        }
      }
      pending = {};
      callersOf = {};
      timer = null;

      if (!events.length) return;

      var payload = JSON.stringify({ events: events });

      // text/plain keeps this a SIMPLE request: sendBeacon cannot perform a
      // CORS preflight, and an application/json body would demand one.
      if (useBeacon && navigator.sendBeacon) {
        navigator.sendBeacon(ENDPOINT, new Blob([payload], { type: 'text/plain;charset=UTF-8' }));
        return;
      }

      if (window.fetch) {
        window.__smReporterFetch = true;
        fetch(ENDPOINT, {
          method: 'POST',
          body: payload,
          headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
          keepalive: true,
          mode: 'cors'
        })['catch'](function () {})
          .then(function () { window.__smReporterFetch = false; }, function () { window.__smReporterFetch = false; });
      }
    } catch (_) { /* never throw out of the reporter */ }
  }

  /* While layout/theme.liquid decides whether to reload for a core script that
     did not arrive (smCoreScriptFailed), what the page throws is held here, not
     sent. A reload drops it with the page: that page recovered, and its cascade
     of "Can't find variable" — the failed script's own report included — is no
     fault worth a card. No reload releases it, and it reports as it would have. */
  var held = [];
  window.__smReleaseHeld = function () {
    var h = held;
    held = [];
    for (var i = 0; i < h.length; i++) report(h[i]);
  };
  // The reload went ahead: what the dying page threw goes with it.
  window.__smDropHeld = function () { held = []; };

  function report(e) {
    try {
      if (window.__smCoreScriptReload) {
        if (held.length < 100) held.push(e);
        return;
      }

      var key = keyOf(e);

      counted[key] = (counted[key] || 0) + 1;
      if (counted[key] > MAX_PER_KEY) return;

      if (!announced[key]) {
        if (keyCount >= MAX_KEYS) return;
        keyCount++;
        announced[key] = true;
        e.firstInSession = true;
      }

      var c = ctx();
      e.page = c.page; e.template = c.template; e.locale = c.locale; e.country = c.country;
      if (LEGACY_ENGINE) e.legacyEngine = true;

      // Batched per caller as well, so two scripts failing the same call in one
      // window both reach byCaller; `announced` above stays per key, so the
      // session is still counted once. At most three callers of one call per
      // window: past that they share the third's batch, rather than each
      // taking one of the twenty events the server reads from a request.
      var batchKey = key;
      var caller = e.request && e.request.caller;
      if (caller) {
        var callers = callersOf[key] || (callersOf[key] = []);
        if (callers.indexOf(caller) === -1 && callers.length < 3) callers.push(caller);
        batchKey = key + '|' + (callers.indexOf(caller) !== -1 ? caller : callers[callers.length - 1]);
      }
      if (pending[batchKey]) {
        pending[batchKey].count++;
      } else {
        pending[batchKey] = { event: e, count: 1 };
      }

      if (!timer) timer = setTimeout(function () { flush(false); }, FLUSH_MS);
    } catch (_) { /* never throw out of the reporter */ }
  }

  // ── drain anything the inline head snippet buffered ───────────────────
  // The reporter is deferred so it never blocks rendering, which means scripts
  // that run before it could throw unobserved. A few hundred bytes inline in
  // <head> buffer those, and this replays them.
  try {
    var early = window.__smErrQ;
    if (early && early.length) {
      for (var i = 0; i < early.length && i < 20; i++) {
        var q = early[i];

        // Slots 6-9 are the event shape the buffer captured for a rejection
        // whose reason is a DOM Event. It records them because String(reason)
        // there is "[object ProgressEvent]" and .target cannot be read back
        // later — the buffer is the only place those values are guaranteed to
        // exist. Formatted HERE, through the same function the live listener
        // uses, so a rejection that beat this script's download groups with an
        // identical one that did not.
        //
        // This is where fingerprint 0821e754bbede025 actually came from: 914
        // sessions of "[object ProgressEvent]", all of it early enough to miss
        // the live handler entirely, which is why describing the reason down
        // there moved almost none of it.
        var eventLike = q[0] === 'rejection' && typeof q[6] === 'string';
        var where = eventLike ? describeEventParts(q[6], q[7], q[8], q[9]) : '';

        report({
          kind: q[0] === 'rejection' ? 'unhandledrejection' : 'js',
          message: (eventLike ? where : q[1]) || 'Unknown error',
          source: (eventLike ? locationOf(where) : q[2]) || '',
          line: q[3],
          column: q[4],
          stack: q[5] ? String(q[5]).slice(0, 4000) : ''
        });
      }
      window.__smErrQ = [];
    }
  } catch (_) {}

  /* Did the server actually serve this resource?
     "Failed to load script" from snowmonkey.com is 3213 sessions in ONE card,
     because resources are fingerprinted by host rather than by filename. Watch
     that card for an hour and the reported file rotates — cart-drawer-note.js,
     then trekkie.storefront.js — while every one of them serves HTTP 200. Not
     one broken asset: whichever asset was in flight when the visitor left.

     responseStatus is the only field consulted, and the absence of a fallback is
     the point. A 404 page HAS a body, so anything size-based (encodedBodySize,
     transferSize) would drop exactly the real failures this path exists to
     catch — a missing script is how the cart drawer once lost its buttons.
     Where the engine does not expose the status (WebKit, Firefox) this returns
     false and behaviour is unchanged, which is the conservative direction.

     Cross-origin resources without Timing-Allow-Origin also report nothing, so
     they too fall through to the old path. */
  /* Did the CALLER declare that it handles this status?

     The wrapper reports every non-ok response the moment it lands, with no way
     for the caller to say "I expected that and recovered". Two cart paths post a
     line key that a concurrent mutation may already have invalidated, take the
     400, re-read /cart.js and retry against the authoritative key —
     cart.js#_recoverStaleCartChange and
     cart-priority-shipping.js#removePriorityLine. Both work; the customer never
     sees anything. The speculative first attempt was still filed as a defect,
     and because a /cart path scores CRITICAL, it was filed as critical on the
     purchase path — 254 sessions sitting on top of the cards a real cart outage
     would appear in.

     Narrow on purpose, because being wrong in the QUIET direction is how a real
     outage gets missed: it suppresses only the exact statuses named, only for
     the call that names them, and the retry never carries it — so a cart that is
     genuinely broken still reports on the second attempt. Anything that is not a
     real array (a bare number, a string) is ignored rather than trusted. fetch
     ignores unknown init properties, so this is inert to the request itself.

     0 means "no answer at all", which is how a failed request is reported, and
     it is declared separately from any HTTP status: [400, 404] does not cover a
     network failure and [0] does not cover a 500. The engraving catalog declares
     [0]. It falls back to the catalog Liquid rendered into the page, and its
     failures were 84 sessions of "GET /api/engraving/catalog → 0" against an API
     that answered every request it received. */
  function callerHandles(init, status) {
    try {
      /* Read the signal as well as init, for the reason __smCartTimeoutMs is
         kept there: app wrappers sitting between our callers and this function
         rebuild the init object, and the signal is the one reference that
         survives that. */
      var expected = (init && init.smExpectedStatuses)
        || (init && init.signal && init.signal.__smExpectedStatuses);
      if (!expected || typeof expected !== 'object' || typeof expected.length !== 'number') {
        return false;
      }
      for (var i = 0; i < expected.length; i++) {
        if (expected[i] === status) return true;
      }
      return false;
    } catch (_) {
      return false;
    }
  }

  /* A request cart.js#cartFetchWithTimeout made, whatever its URL. Its signal
     carries the budget, and no other code on the page sets that mark. */
  function ourCartRequest(init) {
    try {
      return !!(init && init.signal && init.signal.__smCartTimeoutMs);
    } catch (_) {
      return false;
    }
  }

  /* That request's own timer fired: its controller aborts on nothing else.
     Read off the signal, because WebKit rejects an aborted fetch with an
     AbortError of its own ("Fetch is aborted"), never the reason passed to
     abort(), so the mark cartTimeoutReason puts on the reason never arrives. */
  function ourCartTimeoutFired(init) {
    try {
      return !!(init && init.signal && init.signal.aborted && init.signal.__smCartTimeoutMs);
    } catch (_) {
      return false;
    }
  }

  /* Why Shopify refused a cart write. Its 4xx body says it — "… is already
     sold out.", "You can only add 2 … to the cart." — and the customer is shown
     exactly that (product-form.js#handleErrorMessage), but the card knew only
     the status, so "POST /:locale/cart/add → 422" (b825759725912037) could not
     say whether a customer met a sold-out size or something we broke. Shopify
     quotes no address in these, but a gift card's recipient field is in the
     same form, so one is masked rather than sent. */
  var CART_WRITE_RE = /\/cart\/(add|change|update)(\.js)?(\?|#|$)/i;
  var EMAIL_RE = /[^\s@<>"']+@[^\s@<>"']+\.[a-z]{2,}/gi;

  function refusalOf(body) {
    try {
      var text = body && typeof body.description === 'string' && body.description.trim()
        ? body.description
        : (body && typeof body.message === 'string' ? body.message : '');
      // Cut before masking: the pattern is slow on a long run with no "@", and
      // only the first 160 characters are kept anyway.
      return String(text || '').slice(0, 1000).replace(EMAIL_RE, '<email>').replace(/\s+/g, ' ').trim().slice(0, 160);
    } catch (_) {
      return '';
    }
  }

  function isRefusedCartWrite(url, status) {
    return status >= 400 && status < 500 && CART_WRITE_RE.test(String(url));
  }

  /* Report `evt` now, so a page left before the body arrives still sends it,
     and add the reason to the queued event once the body is read — from a
     CLONE, taken before the caller reads its own copy, because this handler was
     attached to the response first. A body that cannot be read leaves no reason. */
  function reportWithRefusal(evt, res) {
    report(evt);
    try {
      res.clone().json().then(function (body) {
        var reason = refusalOf(body);
        if (reason) evt.request.reason = reason;
      }, function () {});
    } catch (_) {}
  }

  /* The same body, read off a finished XHR: as parsed JSON when the caller
     asked for that, as text otherwise. */
  function xhrBodyOf(xhr) {
    try {
      if (xhr.responseType === 'json') return xhr.response;
      if (!xhr.responseType || xhr.responseType === 'text') return JSON.parse(xhr.responseText);
    } catch (_) {}
    return null;
  }

  function resourceTiming(url) {
    try {
      if (!url || !window.performance || !performance.getEntriesByName) return null;
      var entries = performance.getEntriesByName(String(url));
      if (!entries || !entries.length) return null;
      return entries[entries.length - 1];
    } catch (_) {
      return null;
    }
  }

  function resourceWasServed(url) {
    var entry = resourceTiming(url);
    if (!entry) return false;
    var status = entry.responseStatus;
    return typeof status === 'number' && status > 0 && status < 400;
  }

  function isSameOrigin(url) {
    try {
      var target = new URL(String(url), location.href);
      var here = new URL(location.href);
      return target.protocol === here.protocol && target.host === here.host;
    } catch (_) {
      return false;
    }
  }

  /* Nothing ever answered — the request was cancelled or blocked, not missing.

     resourceWasServed() above only silences a load that ANSWERED 2xx/3xx. A
     cancelled request answers nothing at all, so it fell through into the
     page-state guard, and that guard reads the page state at the moment the
     error SURFACES. On a cross-document navigation it is set in time. On the
     shape that dominates our traffic it is not: the script is in flight, the
     visitor backgrounds the tab, Chromium cancels the load, the visitor comes
     BACK, and only then does the error arrive — on a page that visibilityState
     now calls perfectly healthy. That is 89% of "Failed to load script", which
     is 13.8k sessions and the largest theme-owned card we have.

     It is the same blindness `startGeneration` fixed for fetch and XHR, and the
     resource path was left out of that fix because an element has no
     request-start hook to key a generation on. It does not need one: the browser
     already recorded that nothing ever answered.

     SAME-ORIGIN ONLY, and that restriction is the entire safety margin. A
     cross-origin resource served without Timing-Allow-Origin reports
     responseStatus 0 whatever really happened, so there the value means
     "unknowable", not "cancelled" — suppressing on it would silently stop
     reporting genuine third-party outages.

     What this deliberately also drops: a same-origin asset an extension blocked,
     and one that died in a real connection failure. Both are indistinguishable
     from a cancellation from here, and neither is ours to fix.

     Measured against the live theme on 2026-09-08 (Chrome 120): a genuinely
     missing /cdn/shop/t/5/assets/*.js reports responseStatus 404, while a
     request aborted mid-flight reports 0. Cleanly separable, so this costs no
     real 404s — which is the failure this whole path exists to catch. */
  function resourceNeverAnswered(url) {
    if (!isSameOrigin(url)) return false;
    var entry = resourceTiming(url);
    return !!entry && entry.responseStatus === 0;
  }

  // ── uncaught errors ────────────────────────────────────────────────────
  window.addEventListener('error', function (ev) {
    try {
      // A failed <script>/<img>/<link> fires an error event on the ELEMENT and
      // bubbles to window with no message — that is a resource failure, not an
      // exception, and the element's src is the useful part.
      if (ev && ev.target && ev.target !== window && (ev.target.src || ev.target.href)) {
        // The third and last reporting path to get the page-state rule the
        // fetch wrapper got in 526dadd7 and the XHR wrapper in d55f6381.
        //
        // A <script> or <link> still downloading when the visitor navigates
        // away has its request cancelled, and the browser fires an error event
        // on the element — identical to a genuinely missing file. That was 973
        // sessions here, and the giveaway is that the reported filename kept
        // ROTATING across our own assets (shopify-xr.en.js, then page-header.js,
        // then quick-add.js) while every one of them serves HTTP 200, spread
        // evenly over fifteen different route patterns. Not one broken asset:
        // whichever asset happened to be in flight when the page went away.
        //
        // A genuinely missing file fails on a VISIBLE page too, so it still
        // reports. This only drops the ones nobody waited for.
        //
        // Deferred, for the same reason the fetch and XHR paths are: reading
        // leaving() inline is only correct on WebKit. This path kept the
        // synchronous check when the other two were converted, and the counters
        // show exactly the engine skew that predicts — "Failed to load script"
        // for /checkouts/internal/preloads.js is 2,031 sessions, 1,742 of them
        // Windows, Android or unidentified, for a file that serves HTTP 200.
        // Same defect, same fix; this was simply the path I missed.
        // The page-state guard cannot finish this job, because a resource has no
        // request-start hook to key a generation on. But the browser already
        // recorded what the server answered, and that is the thing we actually
        // want: a 404 is our defect, a 200 that still fired `error` was
        // cancelled. Asking removes the guesswork the rotating filename above
        // describes.
        var resourceUrl = ev.target.src || ev.target.href;
        if (resourceWasServed(resourceUrl)) return;
        if (resourceNeverAnswered(resourceUrl)) return;

        reportUnlessLeaving({
          kind: 'resource',
          message: 'Failed to load ' + (ev.target.tagName || 'resource').toLowerCase(),
          source: String(resourceUrl)
        });
        return;
      }

      report({
        kind: 'js',
        message: (ev && ev.message) || 'Unknown error',
        source: (ev && ev.filename) || '',
        line: ev && ev.lineno,
        column: ev && ev.colno,
        stack: ev && ev.error && ev.error.stack ? String(ev.error.stack).slice(0, 4000) : ''
      });
    } catch (_) {}
  }, true);

  // A promise rejected with a DOM Event rather than an Error — the shape you get
  // from `img.onerror = reject`, `xhr.onerror = reject`, `script.onerror =
  // reject` — has no .message, no .stack and no .fileName, and toString()s to
  // "[object ProgressEvent]". Reported naively that is fingerprint
  // 0821e754bbede025: 910 sessions, high severity, on the purchase path, and
  // absolutely nothing in it to act on.
  //
  // The event does know what failed, on .target. Read that.
  //
  // Deliberately reports the ORIGIN and not the full URL: keyOf() groups on
  // message + source, so a per-product image path would shatter one defect into
  // hundreds of fingerprints and bury it. The host is what names a culprit
  // (a widget CDN, an app's extension bundle, our own domain), and it is the
  // question this card actually needs answered.
  /* What failed, and where — as much as can be said without shattering the
     fingerprint into one card per URL.

     Elements get the ORIGIN only. keyOf() groups on message + source, so a
     per-product image or a hashed bundle name would split one defect into
     hundreds of cards and bury it; the host is what names a culprit (a widget
     CDN, an app's extension bundle, our own domain).

     An XHR gets origin + PATH, because there the path is the identity: a
     handful of endpoints, not an open set, and "which endpoint" is the entire
     question. Query string dropped — it carries cart tokens and variant ids.

     __smUrl is what makes the XHR case work at all. A request that fails at the
     network layer never receives a response, so responseURL is still "" at
     rejection time — which is exactly how this first shipped, and it produced
     "Promise rejected with error on XMLHttpRequest" with no location at all.
     The open() wrapper further down records every URL unconditionally, so the
     answer was already on the object. */
  /* The formatting core, taking plain values rather than the event itself.
     Split out because the SAME rejection reaches this file two ways: caught live
     by the listener below, or replayed from the inline buffer in theme.liquid
     that runs before this script is even downloaded. Those two must produce a
     byte-identical message or one defect becomes two fingerprints. */
  function describeEventParts(type, tagName, ctorName, rawUrl) {
    var isElement = !!tagName;
    var tag = isElement ? String(tagName).toLowerCase()
            : ctorName ? String(ctorName)
            : 'unknown';
    var where = '';
    try {
      if (rawUrl) {
        var u = new URL(String(rawUrl), location.href);
        // Elements: origin only. Everything else (an XHR): origin + path.
        where = isElement ? u.origin : u.origin + u.pathname;
      }
    } catch (_) {}
    return 'Promise rejected with ' + (type || 'event') + ' on ' + tag +
           (where ? ' @ ' + where : '');
  }

  function describeEventReason(r) {
    var t = r.target || r.srcElement || null;
    if (!t) return describeEventParts(r.type, null, null, '');
    // responseURL first: after a redirect it is the URL that actually failed,
    // which __smUrl (recorded at open()) would not show.
    var raw = t.currentSrc || t.src || t.href || t.responseURL || t.__smUrl || '';
    return describeEventParts(
      r.type,
      t.tagName,
      t.constructor && t.constructor.name,
      raw
    );
  }

  /* The same location, for the `source` field rather than the message.
     Gives the backend a host to classify — without it every one of these
     rejections has an empty source, falls through to the page URL, and reads as
     a THEME defect however foreign the code actually is. Deliberately the same
     bounded string the message uses, so it cannot split the fingerprint further
     than the message already does. */
  function locationOf(message) {
    var m = / @ (\S+)$/.exec(String(message || ''));
    return m ? m[1] : '';
  }

  function eventReasonSource(r) {
    return locationOf(describeEventReason(r));
  }

  // Duck-typed, not `instanceof Event` / `!(r instanceof Error)`: both are
  // realm-sensitive, and a rejection that crosses an iframe boundary (this page
  // embeds several app iframes) fails the instanceof check against OUR realm
  // while being a perfectly ordinary Event. The absence of a string .message is
  // the realm-independent tell — every Error has one, no Event does.
  function isEventLike(r) {
    return !!r && typeof r === 'object' &&
           typeof r.type === 'string' && typeof r.message !== 'string' &&
           ('target' in r || 'srcElement' in r);
  }

  window.addEventListener('unhandledrejection', function (ev) {
    try {
      var r = ev && ev.reason;
      // OUR cart timeout (cart.js#cartTimeoutReason) that the fetch wrapper
      // below has already filed as the request that timed out — it marks the
      // reason when it does. An app's fetch wrapper that chained onto that
      // request without a handler makes the same rejection unhandled, and it was
      // filed again as "signal is aborted without reason" (87723ab8d7d6110b).
      // Both marks, exactly: a timeout on a URL the wrapper does not watch, or
      // any other AbortError, still reports.
      if (r && r.__smCartTimeout === true && r.__smFiledByWrapper === true) return;
      var eventLike = isEventLike(r);
      report({
        kind: 'unhandledrejection',
        message: eventLike
          ? describeEventReason(r)
          : ((r && (r.message || r.toString())) || 'Unhandled promise rejection'),
        source: (eventLike ? eventReasonSource(r) : '') || (r && r.fileName) || '',
        stack: r && r.stack ? String(r.stack).slice(0, 4000) : ''
      });
    } catch (_) {}
  });

  // ── failed cart / checkout / own-API calls ─────────────────────────────
  // The highest-value signal here: an add-to-cart that fails cleanly with a 422
  // throws no exception at all, so exception logging alone would never see it.
  var WATCH = /\/cart\/|\/cart\.js|\/checkout|app\.snowmonkey\.com\/api\//i;

  function watched(url) {
    try {
      return !!url && WATCH.test(String(url)) && String(url).indexOf('frontend-errors') === -1;
    } catch (_) { return false; }
  }

  /* Which script made a watched request — the first frame of the call stack
     that is not a fetch/XHR wrapper. A failed cart call is filed by its URL,
     and most cart reads on this storefront are not ours: on every page view
     EasyBundle reads /cart.js twice and EasyGift once or twice, all through
     this wrapper (request initiators on the live store, 2026-10-13). So a
     Shopify 5xx on an app's read and on ours land on one card that cannot say
     which. Used by the server only as a breakdown — it never changes what a
     failure is grouped as or who owns it.

     Wrappers are skipped by FILE: this one, the theme's upsell-reinit patch and
     Zipify's (its r.fetch fronts EasyGift's reads), because WebKit prints an
     anonymous wrapper as "@url:line:col" with no name to know it by. A frame
     whose function is named fetch/send/open is skipped too, which catches other
     wrappers in V8 and Gecko. A stack that is all wrappers names the first. */
  var WRAPPER_FILE_RE = /\/(error-reporter|upsell-reinit|zipify-oneclickupsell-extension)\.js(\?|#|:|$)/;
  var WRAPPER_FN_RE = /(^|\.)(fetch|send|open)(\/<)*$/;

  /* A request cart.js marked (see ourCartRequest) is cart.js's, whatever wraps
     fetch above it — so for one of those the theme's cart.js frame is the
     caller. Without this, a wrapper unknown by file and printed anonymously
     was named instead: on "GET /cart → timed out" (2c2b1fcc3c30d8bf), every
     request of which carries the mark, byCaller read EasyGift 9, Shopify 9,
     Bugsnag 7 and cart.js 5. */
  var OUR_CART_FILE_RE = /\/t\/\d+\/assets\/cart\.js(\?|#|:|$)/;

  function callerOf(ours) {
    try {
      // Made in here: reading .stack runs page code (Error.prepareStackTrace),
      // and nothing a page does may throw out of the fetch wrapper.
      var lines = String(new Error().stack || '').split('\n');
      var first = '';
      var picked = '';
      for (var i = 0; i < lines.length && i < 16; i++) {
        var line = lines[i];
        // V8 "    at r.fetch (https://…/x.js:1:2)" / "    at https://…/x.js:1:2";
        // WebKit and Gecko "fetch@https://…/x.js:1:2".
        var m = line.match(/(https?:\/\/[^\s'"()]+?)(?::\d+){1,2}\)?\s*$/);
        if (!m || /\/error-reporter\.js(\?|#|:|$)/.test(m[1])) continue;
        var script = scriptOf(m[1]);
        if (!first) first = script;
        if (ours && OUR_CART_FILE_RE.test(m[1])) return script;
        if (picked || WRAPPER_FILE_RE.test(m[1])) continue;
        var fn = line.replace(/^\s*at\s+(async\s+)?/, '').split(/\s\(|@/)[0];
        fn = fn.replace(/\s*\[as [^\]]*\]$/, '').trim();
        if (WRAPPER_FN_RE.test(fn)) continue;
        if (!ours) return script;
        picked = script;
      }
      return picked || first;
    } catch (_) {
      return '';
    }
  }

  /* A frame's script as something safe to send: no query or hash, and for an
     inline script on our own page not even the page's path — account pages
     (/account/reset/…, /account/orders/…) carry tokens in theirs. */
  function scriptOf(frameUrl) {
    var url = String(frameUrl).split('#')[0].split('?')[0];
    try {
      var u = new URL(url);
      if (u.origin === location.origin && !/\.m?js$/i.test(u.pathname)) return u.origin + '/(inline)';
    } catch (_) {}
    return url.slice(0, 200);
  }

  // The page-lifecycle listeners install BEFORE the request wrappers below.
  // Nothing about them depends on fetch or XHR being patched, and leaving() is
  // what keeps a request the visitor cancelled from being reported as a site
  // failure. Installed after the wrappers, a browser that refuses either patch
  // takes the guard down with it — see the try blocks below.

  // Anything still queued when the visitor leaves goes out as a beacon, which
  // survives the page being torn down.
  // hideGeneration is bumped wherever pageHidden goes true, so a deferred report
  // scheduled before the hide can tell that one happened even after pageshow has
  // reset the flags. See reportUnlessLeaving.
  window.addEventListener('pagehide', function () { pageHidden = true; hideGeneration++; flush(true); });
  window.addEventListener('pageshow', function () { pageHidden = false; leavingUntil = 0; });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') { pageHidden = true; hideGeneration++; flush(true); }
    else { pageHidden = false; }
  });

  // The two signals Chromium DOES give us before it cancels anything. Neither
  // replaces pagehide — on WebKit that still gets there first, and this is
  // additive on purpose.
  window.addEventListener('beforeunload', markLeaving);

  // Where it exists (Chromium), the Navigation API is the better of the two,
  // because it can tell a real document teardown from a same-document
  // navigation. That distinction is the whole reason this is not done by
  // watching link clicks: this theme opens the cart drawer and applies
  // collection filters from anchors it preventDefault()s, and a click-keyed
  // guard would treat "opened the cart drawer" as "left the page" and swallow
  // the /cart.js failures behind it — exactly the defect class we most need to
  // see. A same-document navigation cancels nothing, so it must never mark the
  // page as leaving.
  try {
    if (window.navigation && window.navigation.addEventListener) {
      window.navigation.addEventListener('navigate', function (ev) {
        try {
          if (ev && ev.destination && ev.destination.sameDocument) return;
          markLeaving();
        } catch (_) {}
      });
    }
  } catch (_) {}

  // window.fetch is a non-writable own property on some hardened Android in-app
  // WebViews. This file is strict mode, so the assignment threw and unwound the
  // entire IIFE, taking the XHR wrapper and every lifecycle listener with it.
  // With no pagehide/pageshow/visibilitychange/beforeunload listener, pageHidden
  // never went true, leaving() answered false for the whole session, and the
  // reporter blamed every cancelled request on the site — manufacturing exactly
  // the volume the guard exists to suppress. Refusing one patch must cost that
  // wrapper's data and nothing else.
  try {
    if (window.fetch) {
      var origFetch = window.fetch;
      window.fetch = function (input, init) {
        var url = (input && input.url) || input;
        var method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();

        // Read BEFORE the request goes out. This is the only moment at which the
        // page state is not yet contaminated by whatever happened during the
        // request — see reportUnlessLeaving.
        var genAtStart = hideGeneration;

        var isWatched = watched(url);
        // The cart sections cart.js renders (/cart?section_id=…) are not on the
        // watch list, so their failures are not filed — except OUR timeout,
        // below. It was filed only when an app's fetch wrapper chained onto the
        // request without a handler, as an unhandled rejection from cart.js with
        // the timer for its stack (9d7fd024dd66a4db), though every caller of
        // ours handles it.
        var isOurCartCall = !isWatched && ourCartRequest(init);

        // Read now, while whoever called fetch is still on the stack.
        var caller = (isWatched || isOurCartCall) ? callerOf(ourCartRequest(init)) : '';

        var out = origFetch.apply(this, arguments);

        if (!window.__smReporterFetch && (isWatched || isOurCartCall)) {
          out.then(function (res) {
            if (isWatched && res && !res.ok && !callerHandles(init, res.status)) {
              var evt = { kind: 'network', message: 'HTTP ' + res.status, request: { method: method, url: String(url), status: res.status, caller: caller || undefined } };
              if (isRefusedCartWrite(url, res.status) && typeof res.clone === 'function') reportWithRefusal(evt, res);
              else report(evt);
            }
            return res;
          })['catch'](function (err) {
            // A fetch that dies while the page is hidden or unloading is NOT a
            // failure the customer experienced: the browser cancels in-flight work
            // when it freezes a page for bfcache or navigation. Someone tapping a
            // link mid-request produced "GET /cart.js -> 0" indistinguishable from
            // a real outage.
            //
            // This deliberately keys on the PAGE STATE, not on the error type. The
            // first version of this guard tested err.name === 'AbortError' and
            // missed almost all of it, because the browsers disagree on how they
            // report the same event: Safari rejects with an AbortError ("Fetch is
            // aborted"), Chrome rejects with a plain TypeError ("Failed to fetch").
            // So the /:locale/cart.js fingerprint (Safari-heavy) dropped 88% while
            // /cart.js (Android/Chrome-heavy, 112 sessions) did not move at all.
            // Page state is the thing we actually know; the error name is not.
            //
            // Still narrow: ANY rejection on a VISIBLE page is reported, including
            // cartFetchWithTimeout's 15s timeout, because there the customer really
            // did wait. A genuine outage also reaches plenty of visible pages, so
            // this cannot hide one — and a real server failure shows up as 5xx/429
            // through the res.ok branch above regardless of page state.
            // Deferred rather than immediate: on Chromium this rejection arrives
            // BEFORE pagehide, so an inline leaving() check reads a flag that is
            // still false. See reportUnlessLeaving.
            //
            // Report, but do NOT re-throw. This is a DERIVED promise chain that
            // nobody awaits — the original is what the caller got back. Throwing
            // here rejects a promise with no handler, which fires
            // unhandledrejection and makes the reporter manufacture the very
            // errors it is meant to observe. It did: 45 sessions of "Failed to
            // fetch" with this file in the stack, from calls the app handled fine.
            //
            // A caller that recovers from getting no answer at all says so with
            // 0, the status this branch reports. The engraving catalog does: it
            // keeps the catalog already in the page (see callerHandles).
            // The unhandled copy of OUR cart timeout is recognised by this mark —
            // see the unhandledrejection listener above.
            var isOurTimeout = false;
            try {
              isOurTimeout = (!!err && err.__smCartTimeout === true) || ourCartTimeoutFired(init);
              // Marked whatever arrived — WebKit's own AbortError included —
              // since every chain on this request rejects with that same object.
              if (isOurTimeout && err && typeof err === 'object') {
                err.__smCartTimeout = true;
                err.__smFiledByWrapper = true;
              }
            } catch (_) {}
            // An unwatched cart section is filed for our timeout only.
            if (!isWatched && !isOurTimeout) return;
            if (callerHandles(init, 0)) return;
            // OUR OWN 15s timeout, not the browser cancelling in-flight work.
            // cart.js marks its AbortSignal (see cartFetchWithTimeout), and that
            // controller aborts on nothing else, so the mark plus .aborted is
            // unambiguous.
            //
            // Reported under its own message so it stops hiding inside the
            // status-0 pile. The two look identical from here and mean opposite
            // things: a cancelled request is somebody who navigated away, this is
            // somebody who watched the cart do nothing for fifteen seconds. Worth
            // its own fingerprint precisely because it is the half that is real.
            var waitedMs = 0;
            try {
              if (init && init.signal && init.signal.aborted && init.signal.__smCartTimeoutMs) {
                // Measured elapsed where cart.js could record it, nominal budget only
                // as a fallback. A value far above the budget means the timer was
                // throttled or frozen, i.e. teardown rather than a customer waiting.
                waitedMs = init.signal.__smCartWaitedMs || init.signal.__smCartTimeoutMs;
              }
            } catch (_) {}

            // Sent as a FLAG on the request, not as a message: a network event's
            // stored message and fingerprint are both rebuilt from method + path +
            // status on the server, so a custom message here would be discarded.
            reportUnlessLeaving({
              kind: 'network',
              message: (err && err.message) || 'Network request failed',
              request: {
                method: method,
                url: String(url),
                status: 0,
                timedOut: waitedMs > 0,
                waitedMs: waitedMs || undefined,
                caller: caller || undefined
              }
            }, genAtStart);
          });
        }

        return out;
      };
    }
  } catch (_) {}

  // Same guard, separately: an extension can freeze XMLHttpRequest.prototype.
  try {
    var origOpen = XMLHttpRequest.prototype.open;
    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      try { this.__smMethod = method; this.__smUrl = url; } catch (_) {}
      return origOpen.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      try {
        var xhr = this;
        if (watched(xhr.__smUrl)) {
          // Same reason as the fetch wrapper: captured at send, not at loadend,
          // so a hide DURING the request is still visible afterwards.
          var genAtSend = hideGeneration;
          var callerAtSend = callerOf();
          xhr.addEventListener('loadend', function () {
            try {
              // The same page-lifecycle rule as the fetch wrapper above, which
              // this path never got. A request the browser cancels while the page
              // is hidden or tearing down is not a failure the customer
              // experienced: iOS Safari aborts everything in flight when it
              // freezes a page, and an aborted XHR arrives here as status 0,
              // indistinguishable from a real network failure unless you also look
              // at the page state.
              //
              // That is all "POST /cart/update.js -> 0" ever was — 17 sessions,
              // every one of them WebKit (16 iOS, 1 macOS), from app cart writes
              // on pages the visitor was simply leaving. The fetch side was fixed
              // in 526dadd7 and this side was left behind, so the noise moved
              // rather than stopped.
              //
              // Only status 0 is gated. A genuine server failure still arrives as
              // 4xx/5xx and is reported whatever the page is doing — which is why
              // the two branches below take different paths: status 0 defers the
              // page-state decision (Chromium fires loadend before pagehide too),
              // while a real 4xx/5xx reports immediately and unconditionally.
              if (xhr.status === 0 || xhr.status >= 400) {
                var evt = {
                  kind: 'network',
                  message: xhr.status ? 'HTTP ' + xhr.status : 'Network request failed',
                  request: { method: String(xhr.__smMethod || 'GET').toUpperCase(), url: String(xhr.__smUrl), status: xhr.status, caller: callerAtSend || undefined }
                };
                if (isRefusedCartWrite(xhr.__smUrl, xhr.status)) {
                  var reason = refusalOf(xhrBodyOf(xhr));
                  if (reason) evt.request.reason = reason;
                }
                if (xhr.status === 0) reportUnlessLeaving(evt, genAtSend);
                else report(evt);
              }
            } catch (_) {}
          });
        }
      } catch (_) {}
      return origSend.apply(this, arguments);
    };
  } catch (_) {}

})();
